import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";
import { Building2, Plus, Edit2, Pause, Play, Ban, X, Loader2, CheckCircle2, AlertTriangle, ShieldCheck, Wrench, XCircle, Clock, Trash2, Gift } from "lucide-react";
import ImgFallback from "../../components/ImgFallback";

const ESTATUS_META = {
  pendiente_comprobante: { label: "Sin comprobante", cls: "eo-pill-danger", icon: AlertTriangle },
  pendiente_aprobacion: { label: "Por aprobar", cls: "eo-pill-warning", icon: Clock },
  activa: { label: "Activa", cls: "eo-pill-success", icon: CheckCircle2 },
  suspendida: { label: "Suspendida", cls: "eo-pill-warning", icon: AlertTriangle },
  rechazada: { label: "Rechazada", cls: "eo-pill-danger", icon: XCircle },
  cancelada: { label: "Cancelada", cls: "eo-pill-danger", icon: Ban },
};

const PENDIENTES = new Set(["pendiente_comprobante", "pendiente_aprobacion"]);

const AdminEmpresas = () => {
  const [empresas, setEmpresas] = useState([]);
  const [planes, setPlanes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState({ nombre: "", dueno_nombre: "", dueno_email: "", dueno_password: "", plan_id: "plan_organiza", ubicacion: "", telefono: "" });

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [e, p] = await Promise.all([axios.get(`${API}/admin/empresas`), axios.get(`${API}/admin/planes`)]);
      setEmpresas(e.data || []); setPlanes(p.data || []);
    } catch (err) { toast.error("Acceso denegado o error: " + (err?.response?.data?.detail || err.message)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  // === Utilidades de plataforma (estabilización iter59) ===
  const [setupLoading, setSetupLoading] = useState(false);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditReport, setAuditReport] = useState(null);
  const [showAudit, setShowAudit] = useState(false);

  const inicializarCelebraGroup = async () => {
    if (!window.confirm("Inicializar/vincular la empresa interna 'Celebra Group' al usuario actual.\n\nEs idempotente: si ya existe, solo actualiza el flag. Seguro de ejecutar.")) return;
    setSetupLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/celebra-group/setup`);
      toast.success(`Celebra Group ${data.creada ? "creada" : "actualizada"} · ${data.empresa_id}`);
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setSetupLoading(false); }
  };

  const auditarIntegridad = async () => {
    setAuditLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/integridad`);
      setAuditReport(data);
      setShowAudit(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setAuditLoading(false); }
  };

  const limpiarIntegridad = async (dryRun = true) => {
    const acciones = [
      "crear_cliente_historico",
      "limpiar_contrato_id_invalido",
      "limpiar_cliente_id_invalido_pagos",
      "limpiar_cliente_id_invalido_contratos",
      "fusionar_clientes_duplicados",
    ];
    if (!dryRun && !window.confirm("Ejecutar LIMPIEZA REAL de inconsistencias?\n\nEsto:\n• Crea un 'Cliente histórico' para reasignar eventos con cliente_id huérfano\n• Limpia FKs inválidas (set null)\n• Fusiona clientes duplicados por email+tenant (mantiene el más antiguo)\n\nNINGÚN dato productivo se borra. Continúa solo si revisaste el dry-run.")) return;
    setAuditLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/integridad/limpiar`, { dry_run: dryRun, acciones });
      const cambios = data.cambios || {};
      const summary = Object.entries(cambios).map(([k, v]) => `${k}: ${typeof v === "object" ? JSON.stringify(v) : v}`).join(" · ");
      toast.success(`${dryRun ? "DRY-RUN" : "LIMPIEZA"} · ${summary || "sin cambios"}`, { duration: 8000 });
      if (!dryRun) {
        // recargar el reporte
        const { data: r } = await axios.get(`${API}/admin/integridad`);
        setAuditReport(r);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setAuditLoading(false); }
  };

  const abrirNuevo = () => { setEditando(null); setForm({ nombre: "", dueno_nombre: "", dueno_email: "", dueno_password: "", plan_id: planes[0]?.plan_id || "plan_organiza", ubicacion: "", telefono: "" }); setShowModal(true); };
  const abrirEdit = (e) => { setEditando(e); setForm({ nombre: e.nombre, plan_id: e.plan_id, ubicacion: e.ubicacion || "", telefono: e.telefono || "" }); setShowModal(true); };

  const guardar = async () => {
    try {
      if (editando) {
        await axios.put(`${API}/admin/empresas/${editando.empresa_id}`, { nombre: form.nombre, plan_id: form.plan_id, ubicacion: form.ubicacion, telefono: form.telefono });
        toast.success("Empresa actualizada");
      } else {
        if (!form.nombre || !form.dueno_email || !form.dueno_password) { toast.error("Nombre empresa + email + password del dueño son obligatorios"); return; }
        const { data } = await axios.post(`${API}/admin/empresas`, form);
        toast.success(`Empresa creada · Dueño: ${data.empresa.dueno.email}`);
      }
      setShowModal(false); fetchAll();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };

  const aprobarEmpresa = async (emp) => {
    const opcion = window.prompt(
      `Aprobar "${emp.nombre}"?\n\nOpciones:\n- Deja vacío para aprobar con pago confirmado (30 días)\n- Escribe 7, 15 o 30 para activar como prueba gratuita`,
      ""
    );
    if (opcion === null) return;
    const dias_trial = parseInt(opcion, 10);
    try {
      const body = !isNaN(dias_trial) && dias_trial > 0 ? { dias_trial } : {};
      const { data } = await axios.post(`${API}/admin/empresas/${emp.empresa_id}/aprobar`, body);
      toast.success(`${emp.nombre} activada · ${data.modo === "trial" ? `Trial hasta ${data.fecha_vencimiento}` : "Suscripción activa"}`);
      fetchAll();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };

  const extenderTrialEmpresa = async (emp) => {
    const opcion = window.prompt(
      `Otorgar / extender prueba a "${emp.nombre}"?\n\nEscribe los días (7, 15, 30 o cualquier número). Vacío = cancelar.`,
      "15"
    );
    if (opcion === null) return;
    const dias_trial = parseInt(opcion, 10);
    if (isNaN(dias_trial) || dias_trial <= 0) { toast.error("Días inválidos"); return; }
    try {
      const { data } = await axios.post(`${API}/admin/empresas/${emp.empresa_id}/extender-trial`, { dias_trial });
      toast.success(`${emp.nombre} · Trial hasta ${data.prueba_hasta?.substring(0, 10) || "—"}`);
      fetchAll();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };

  const rechazarEmpresa = async (emp) => {
    const motivo = window.prompt(`Rechazar "${emp.nombre}"?\nMotivo (opcional):`, "");
    if (motivo === null) return;
    try {
      await axios.post(`${API}/admin/empresas/${emp.empresa_id}/rechazar`, { motivo });
      toast.success(`${emp.nombre} rechazada`);
      fetchAll();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };

  const accion = async (emp, tipo) => {
    const conf = {
      suspender: { msg: `¿Suspender "${emp.nombre}"? Los usuarios perderán acceso temporalmente.`, url: `${API}/admin/empresas/${emp.empresa_id}/suspender`, method: "post" },      reactivar: { msg: `¿Reactivar "${emp.nombre}"?`, url: `${API}/admin/empresas/${emp.empresa_id}/reactivar`, method: "post" },
      cancelar: { msg: `⚠️ ¿CANCELAR "${emp.nombre}"? Esta acción cancela la suscripción (reversible).`, url: `${API}/admin/empresas/${emp.empresa_id}`, method: "delete" },
      eliminar: { msg: `🗑️ ¿ELIMINAR DEFINITIVAMENTE "${emp.nombre}"? Se borra la empresa y la suscripción, y se desactivan los usuarios. Esta acción NO se puede revertir automáticamente (queda snapshot en auditoría).`, url: `${API}/admin/empresas/${emp.empresa_id}/eliminar-definitivo`, method: "post" },
    }[tipo];
    if (!window.confirm(conf.msg)) return;
    try {
      await axios[conf.method](conf.url);
      toast.success(`Empresa ${tipo === "cancelar" ? "cancelada" : tipo === "eliminar" ? "eliminada" : tipo + "da"}`);
      fetchAll();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin" style={{ color: "var(--brand-primary)" }} /></div>;

  return (
    <div className="max-w-6xl mx-auto pb-12">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
        <div>
          <p className="text-[11px] uppercase tracking-[0.18em] mb-1" style={{ color: "var(--brand-primary)" }}>Superadmin</p>
          <h1 className="font-display text-4xl md:text-5xl leading-none" style={{ color: "var(--brand-text)" }}>Empresas & Planes</h1>
          <p className="text-sm mt-2" style={{ color: "var(--brand-text-muted)" }}>Gestiona las empresas suscritas al sistema. Cada empresa tiene sus datos aislados.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={inicializarCelebraGroup} disabled={setupLoading} className="eo-btn eo-btn-outline" title="Crea/actualiza la empresa interna Celebra Group y la vincula al superadmin. Ejecutar 1 vez tras cada deploy." data-testid="btn-init-celebra-group">
            {setupLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
            Inicializar Celebra Group
          </button>
          <button onClick={auditarIntegridad} disabled={auditLoading} className="eo-btn eo-btn-outline" title="Audita FKs huérfanas y duplicados en toda la plataforma." data-testid="btn-audit-integridad">
            {auditLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wrench className="w-4 h-4" />}
            Auditoría integridad
          </button>
          <button onClick={abrirNuevo} className="eo-btn eo-btn-primary" data-testid="btn-nueva-empresa"><Plus className="w-4 h-4" /> Nueva empresa</button>
        </div>
      </div>

      {/* Planes */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
        {planes.filter(p => p.activo !== false).slice(0, 3).map(p => (
          <div key={p.plan_id} className="eo-card p-5">
            <p className="text-[10px] uppercase tracking-[0.18em]" style={{ color: "var(--brand-text-muted)" }}>Plan</p>
            <h3 className="font-display text-xl mt-1" style={{ color: "var(--brand-text)" }}>{p.nombre}</h3>
            <p className="font-display text-3xl mt-2" style={{ color: "var(--brand-primary)" }}>${p.precio || p.precio_mensual}<span className="text-xs" style={{ color: "var(--brand-text-muted)" }}>/{p.periodo || "mes"}</span></p>
            <p className="text-[11px] mt-2" style={{ color: "var(--brand-text-muted)" }}>{(p.modulos || []).length} módulos · {empresas.filter(e => e.plan_id === p.plan_id).length} empresas activas</p>
          </div>
        ))}
      </div>

      {/* Banner empresas por aprobar */}
      {empresas.filter(e => PENDIENTES.has(e.estatus)).length > 0 && (
        <div className="bg-amber-50 border-2 border-amber-300 rounded-2xl p-4 mb-4" data-testid="banner-por-aprobar">
          <div className="flex items-center gap-3">
            <Clock className="w-6 h-6 text-amber-700 shrink-0" />
            <div className="flex-1">
              <p className="font-display text-lg text-amber-900">
                {empresas.filter(e => PENDIENTES.has(e.estatus)).length} empresa(s) esperando tu revisión
              </p>
              <p className="text-xs text-amber-700">Revisa el comprobante de pago y aprueba (con o sin trial de 7/15/30 días).</p>
            </div>
          </div>
        </div>
      )}

      {/* Tabla empresas */}
      <div className="eo-card overflow-hidden">
        <div className="px-5 py-4 flex items-center justify-between" style={{ borderBottom: "1px solid var(--brand-border)" }}>
          <h3 className="font-display text-lg" style={{ color: "var(--brand-text)" }}>Empresas ({empresas.length})</h3>
        </div>
        {empresas.length === 0 ? (
          <div className="p-12 text-center"><Building2 className="w-10 h-10 mx-auto mb-2" style={{ color: "var(--brand-text-muted)", opacity: 0.4 }} /><p className="text-sm" style={{ color: "var(--brand-text-muted)" }}>Aún no hay empresas. Crea la primera.</p></div>
        ) : (
          <div className="divide-y" style={{ borderColor: "var(--brand-border)" }}>
            {empresas.map(e => {
              const meta = ESTATUS_META[e.estatus] || { label: e.estatus || "—", cls: "eo-pill", icon: Clock };
              const esPendiente = PENDIENTES.has(e.estatus);
              return (
                <div key={e.empresa_id} className="p-4 flex items-center gap-3 hover:bg-[var(--brand-surface-muted)] flex-wrap md:flex-nowrap" data-testid={`emp-${e.empresa_id}`}>
                  <div className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0" style={{ background: "var(--brand-primary-soft)" }}><Building2 className="w-5 h-5" style={{ color: "var(--brand-primary)" }} /></div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate" style={{ color: "var(--brand-text)" }}>{e.nombre}</p>
                    <p className="text-[11px]" style={{ color: "var(--brand-text-muted)" }}>{e.dueno?.nombre || "—"} · {e.dueno?.email || "—"}</p>
                    <p className="text-[11px]" style={{ color: "var(--brand-text-muted)" }}>{e.telefono ? `Tel: ${e.telefono} · ` : ""}{e.ubicacion || e.tipo_negocio || "—"}</p>
                    {esPendiente && (
                      <div className="mt-2 flex items-start gap-3 flex-wrap">
                        {e.comprobante_url ? (
                          <a
                            href={e.comprobante_url}
                            target="_blank"
                            rel="noreferrer"
                            className="block rounded-lg overflow-hidden border border-amber-300 bg-amber-50 hover:border-amber-500 transition shrink-0"
                            data-testid={`comprobante-${e.empresa_id}`}
                            title="Click para ver completo"
                          >
                            <ImgFallback
                              src={e.comprobante_url}
                              alt="Comprobante de pago"
                              className="w-24 h-24 object-cover"
                              fallback={(
                                <div className="w-24 h-24 flex flex-col items-center justify-center text-center p-2 text-[10px] text-red-700 bg-red-50">
                                  <span className="text-lg">⚠</span>
                                  <span>Ver comprobante</span>
                                  <span className="text-stone-500 mt-0.5">(archivo)</span>
                                </div>
                              )}
                            />
                          </a>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-red-100 text-red-700 text-xs" data-testid={`sin-comprobante-${e.empresa_id}`}>
                            ⚠ Sin comprobante — pídelo por WhatsApp
                          </span>
                        )}
                        <div className="flex flex-col gap-1 text-xs">
                          {e.monto_pagado && <span className="text-stone-700 font-medium">${Number(e.monto_pagado).toLocaleString("es-MX")} MXN</span>}
                          {e.metodo_pago && <span className="text-stone-500 capitalize">{e.metodo_pago}</span>}
                          {e.comprobante_url && (
                            <a href={e.comprobante_url} target="_blank" rel="noreferrer" className="text-amber-700 hover:underline text-[11px]">Abrir original ↗</a>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  <span className={`eo-pill ${meta.cls}`}><meta.icon className="w-3 h-3" /> {meta.label}</span>
                  <div className="text-right shrink-0 min-w-[140px]">
                    <p className="text-xs font-medium" style={{ color: "var(--brand-primary)" }}>{e.plan?.nombre || "Sin plan"} {e.plan?.precio || e.plan?.precio_mensual ? `· $${e.plan?.precio || e.plan?.precio_mensual}` : ""}</p>
                    {(e.fecha_alta || e.created_at) && <p className="text-[10px]" style={{ color: "var(--brand-text-muted)" }}>Alta: {(e.fecha_alta || e.created_at || "").substring(0, 10)}</p>}
                    {e.prueba_hasta && <p className="text-[10px]" style={{ color: "#d97706" }}>Prueba hasta: {(e.prueba_hasta || "").substring(0, 10)}</p>}
                    {e.fecha_vencimiento && <p className="text-[10px]" style={{ color: "var(--brand-text-muted)" }}>Vence: {(e.fecha_vencimiento || "").substring(0, 10)}</p>}
                  </div>
                  <div className="flex gap-1 shrink-0">
                    {esPendiente && (
                      <>
                        <button onClick={() => aprobarEmpresa(e)} aria-label={`Aprobar empresa ${e.nombre}`} className="p-2 rounded-lg bg-emerald-50 hover:bg-emerald-100" title="Aprobar (30 días o trial 7/15/30)" data-testid={`btn-aprobar-empresa-${e.empresa_id}`}>
                          <CheckCircle2 className="w-4 h-4 text-emerald-700" />
                        </button>
                        <button onClick={() => rechazarEmpresa(e)} aria-label={`Rechazar empresa ${e.nombre}`} className="p-2 rounded-lg bg-red-50 hover:bg-red-100" title="Rechazar empresa" data-testid={`btn-rechazar-empresa-${e.empresa_id}`}>
                          <XCircle className="w-4 h-4 text-red-600" />
                        </button>
                      </>
                    )}
                    {(e.estatus === "activa" || e.estatus === "suspendida") && (
                      <button onClick={() => extenderTrialEmpresa(e)} className="p-2 rounded-lg bg-amber-50 hover:bg-amber-100" title="Dar / extender prueba gratis (7/15/30 días)" data-testid={`btn-trial-${e.empresa_id}`}>
                        <Gift className="w-4 h-4 text-amber-700" />
                      </button>
                    )}
                    <button onClick={() => abrirEdit(e)} className="p-2 rounded-lg hover:bg-[var(--brand-primary-soft)]" title="Editar" data-testid={`edit-${e.empresa_id}`}><Edit2 className="w-4 h-4" style={{ color: "var(--brand-primary)" }} /></button>
                    {e.estatus === "activa" && <button onClick={() => accion(e, "suspender")} className="p-2 rounded-lg hover:bg-[var(--brand-warning-soft)]" title="Suspender" data-testid={`susp-${e.empresa_id}`}><Pause className="w-4 h-4" style={{ color: "var(--brand-warning)" }} /></button>}
                    {e.estatus === "suspendida" && <button onClick={() => accion(e, "reactivar")} className="p-2 rounded-lg hover:bg-[var(--brand-success-soft)]" title="Reactivar" data-testid={`react-${e.empresa_id}`}><Play className="w-4 h-4" style={{ color: "var(--brand-success)" }} /></button>}
                    {e.estatus !== "cancelada" && <button onClick={() => accion(e, "cancelar")} className="p-2 rounded-lg hover:bg-red-50" title="Cancelar" data-testid={`cancel-${e.empresa_id}`}><Ban className="w-4 h-4 text-red-500" /></button>}
                    {!e.es_celebra_group && <button onClick={() => accion(e, "eliminar")} className="p-2 rounded-lg hover:bg-red-100" title="Eliminar definitivamente" data-testid={`delete-${e.empresa_id}`}><Trash2 className="w-4 h-4 text-red-700" /></button>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-[200] bg-black/50 flex items-center justify-center p-4" onClick={() => setShowModal(false)}>
          <div className="w-full max-w-md rounded-3xl overflow-hidden" style={{ background: "var(--brand-surface-card)" }} onClick={(ev) => ev.stopPropagation()}>
            <div className="px-5 py-4 flex items-center justify-between" style={{ borderBottom: "1px solid var(--brand-border)" }}>
              <h3 className="font-display text-xl" style={{ color: "var(--brand-text)" }}>{editando ? "Editar empresa" : "Nueva empresa"}</h3>
              <button onClick={() => setShowModal(false)} className="p-2 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-3">
              <F label="Nombre de la empresa *" v={form.nombre} on={(v) => setForm({ ...form, nombre: v })} tid="e-nombre" />
              <div>
                <label className="text-[10px] uppercase tracking-[0.14em] block mb-1" style={{ color: "var(--brand-text-muted)" }}>Plan</label>
                <select value={form.plan_id} onChange={(ev) => setForm({ ...form, plan_id: ev.target.value })} className="eo-input" data-testid="e-plan">
                  {planes.filter(p => p.activo !== false).map(p => <option key={p.plan_id} value={p.plan_id}>{p.nombre} · ${p.precio || p.precio_mensual}/mes</option>)}
                </select>
              </div>
              {!editando && (
                <>
                  <div className="pt-3" style={{ borderTop: "1px dashed var(--brand-border)" }}>
                    <p className="text-[10px] uppercase tracking-[0.14em] mb-2" style={{ color: "var(--brand-text-muted)" }}>Datos del dueño (se creará usuario admin)</p>
                  </div>
                  <F label="Nombre del dueño" v={form.dueno_nombre} on={(v) => setForm({ ...form, dueno_nombre: v })} tid="e-d-nombre" />
                  <F label="Email del dueño *" v={form.dueno_email} on={(v) => setForm({ ...form, dueno_email: v })} tid="e-d-email" />
                  <F label="Contraseña inicial *" type="password" v={form.dueno_password} on={(v) => setForm({ ...form, dueno_password: v })} tid="e-d-pass" />
                </>
              )}
              <F label="Ubicación" v={form.ubicacion} on={(v) => setForm({ ...form, ubicacion: v })} tid="e-ubic" />
              <F label="Teléfono" v={form.telefono} on={(v) => setForm({ ...form, telefono: v })} tid="e-tel" />
            </div>
            <div className="p-4 flex gap-2 justify-end" style={{ borderTop: "1px solid var(--brand-border)" }}>
              <button onClick={() => setShowModal(false)} className="eo-btn eo-btn-outline">Cancelar</button>
              <button onClick={guardar} className="eo-btn eo-btn-primary" data-testid="e-guardar">{editando ? "Actualizar" : "Crear empresa"}</button>
            </div>
          </div>
        </div>
      )}
      {showAudit && auditReport && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setShowAudit(false)}>
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[85vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="modal-auditoria">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="font-display text-2xl" style={{ color: "var(--brand-text)" }}>Auditoría de integridad</h2>
                <p className="text-xs mt-1" style={{ color: "var(--brand-text-muted)" }}>Inconsistencias en FKs y duplicados</p>
              </div>
              <button onClick={() => setShowAudit(false)} className="p-1"><X className="w-5 h-5" /></button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4">
              {Object.entries(auditReport.totales).map(([k, v]) => (
                <div key={k} className="border border-stone-200 rounded-lg p-2">
                  <p className="text-[10px] uppercase text-stone-500">{k}</p>
                  <p className="font-display text-xl text-stone-900">{v}</p>
                </div>
              ))}
            </div>
            <h3 className="text-sm font-medium uppercase tracking-wider mb-2" style={{ color: "var(--brand-text-muted)" }}>Huérfanos</h3>
            <div className="space-y-1 mb-4">
              {Object.entries(auditReport.huerfanos).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between text-sm border-b border-stone-100 py-1">
                  <span className="text-stone-700">{k.replace(/_/g, " ")}</span>
                  <span className={`font-medium ${v.count > 0 ? "text-amber-700" : "text-emerald-700"}`}>{v.count}</span>
                </div>
              ))}
              <div className="flex items-center justify-between text-sm pt-2">
                <span className="text-stone-700">duplicados clientes (email+tenant)</span>
                <span className={`font-medium ${auditReport.duplicados.clientes_por_email_y_tenant > 0 ? "text-amber-700" : "text-emerald-700"}`}>{auditReport.duplicados.clientes_por_email_y_tenant}</span>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 pt-4 border-t border-stone-200">
              <button onClick={() => limpiarIntegridad(true)} disabled={auditLoading} className="eo-btn eo-btn-outline" data-testid="btn-dry-run">
                {auditLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wrench className="w-4 h-4" />}
                Simular limpieza (dry-run)
              </button>
              <button onClick={() => limpiarIntegridad(false)} disabled={auditLoading} className="eo-btn eo-btn-primary" data-testid="btn-limpiar-real">
                {auditLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                Ejecutar limpieza real
              </button>
              <button onClick={() => setShowAudit(false)} className="eo-btn eo-btn-ghost ml-auto">Cerrar</button>
            </div>
            <p className="text-[11px] text-stone-500 mt-3">La limpieza nunca borra datos: crea un &quot;Cliente histórico&quot; placeholder y limpia FKs inválidas con $unset. Los clientes duplicados se fusionan manteniendo el más antiguo.</p>
          </div>
        </div>
      )}
    </div>
  );
};

const F = ({ label, v, on, tid, type = "text" }) => (
  <div>
    <label className="text-[10px] uppercase tracking-[0.14em] block mb-1" style={{ color: "var(--brand-text-muted)" }}>{label}</label>
    <input type={type} value={v ?? ""} onChange={(e) => on(e.target.value)} className="eo-input" data-testid={tid} />
  </div>
);

export default AdminEmpresas;
